import { useCallback } from 'react';
import { useSignalConnection } from '~/components/Signals/SignalsProvider';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { SignalMessages } from '~/server/common/enums';
import { useMetricSignalsStore } from '~/store/metric-signals.store';
import type { MetricEntityType, MetricType, MetricUpdatePayload } from './metric-signals.types';

/**
 * Maps raw reaction names from the metric-event-watcher (e.g. 'Like')
 * to the MetricType format used on the frontend (e.g. 'likeCount').
 */
const reactionToMetricType: Record<string, MetricType> = {
  Like: 'likeCount',
  Dislike: 'dislikeCount',
  Heart: 'heartCount',
  Laugh: 'laughCount',
  Cry: 'cryCount',
};

function normalizeMetricPayload(raw: Record<string, number>): MetricUpdatePayload {
  const normalized: MetricUpdatePayload = {};
  for (const [key, value] of Object.entries(raw)) {
    const metricType = (reactionToMetricType[key] ?? key) as MetricType;
    normalized[metricType] = (normalized[metricType] ?? 0) + value;
  }
  return normalized;
}

/**
 * Listens for metric:update signals and applies deltas to the store.
 * The signal payload contains the delta values (not absolute values).
 */
export function useMetricSignalsListener() {
  const applyDelta = useMetricSignalsStore((state) => state.applyDelta);
  const currentUser = useCurrentUser();
  const currentUserId = currentUser?.id;

  const handleMetricUpdate = useCallback(
    (
      payload: Record<string, any> & {
        entityType?: MetricEntityType;
        entityId?: number;
        userId?: number;
      }
    ) => {
      if (!payload.entityType || !payload.entityId) {
        console.warn('MetricSignalsRegistrar: missing entityType or entityId in payload', payload);
        return;
      }

      // Echo-suppression: skip deltas the current user originated. The optimistic
      // reaction store (ReactionButton) already accounts for the user's own
      // action, so applying the signal echo on top would double-count it.
      if (payload.userId != null && payload.userId === currentUserId) return;

      // Pull `userId` out so it isn't mistaken for a metric by normalizeMetricPayload.
      const { entityType, entityId, userId: _userId, ...rawUpdates } = payload;
      const updates = normalizeMetricPayload(rawUpdates);
      // Skip empty/no-op payloads — the server sometimes emits topic-only
      // notifications with no metric values, and applyDelta would otherwise
      // produce a fresh `deltas` object reference that fans out a selector
      // run to every subscribed consumer.
      const hasChange = Object.values(updates).some((v) => !!v);
      if (!hasChange) return;
      applyDelta(entityType, entityId, updates);
    },
    [applyDelta, currentUserId]
  );

  useSignalConnection(SignalMessages.MetricUpdate, handleMetricUpdate);
}
